import React, { useState, useEffect } from 'react';
import { Card, Button, App, Alert, Radio, Space, Typography, Progress, Divider } from 'antd';
import { ExclamationCircleOutlined, LogoutOutlined } from '@ant-design/icons';
import { trpc } from '../../lib/trpc';
import type { Company } from '../../types/company';
import CompanyHeaderImageSettings from './CompanyHeaderImageSettings';
import CompanySealSettings from './CompanySealSettings';
import { useCompanyStore } from '../../stores/companyStore';
import { withDevModalFilePath } from '../common';
import WithdrawModal from './WithdrawModal';

const { Text, Paragraph } = Typography;

interface CompanySettingsTabProps {
  company?: Company;
  isManager: boolean;
  isAdmin: boolean;
}

const { Modal } = withDevModalFilePath('client/src/components/company/CompanySettingsTab.tsx');

const CompanySettingsTab: React.FC<CompanySettingsTabProps> = ({ company, isManager, isAdmin }) => {
  const [showWarning, setShowWarning] = useState(false);
  const [pendingMode, setPendingMode] = useState<'complex' | 'manufacturer_lifespan' | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
  const utils = trpc.useUtils();
  const { message } = App.useApp();

  // 退会資格の確認
  const { data: withdrawEligibility } = trpc.company.canWithdraw.useQuery(undefined, {
    enabled: isManager,
  });

  const { setHealthManagementMode } = useCompanyStore();

  const { data: currentMode, refetch: refetchMode } = trpc.company.getHealthManagementMode.useQuery();

  const { data: jobStatus } = trpc.company.getRecalculateJobStatus.useQuery(
    { jobId: jobId! },
    {
      enabled: !!jobId,
      refetchInterval: (query) => {
        const state = query.state.data?.state;
        if (state === 'completed' || state === 'failed') {
          return false;
        }
        return 2000; // 2秒ごとにポーリング
      },
    }
  );

  const updateHealthModeMutation = trpc.company.updateHealthManagementMode.useMutation();

  // ジョブ完了/失敗時の処理
  useEffect(() => {
    if (jobStatus?.state === 'completed') {
      message.success('健全度の再計算が完了しました');
      refetchMode();
      setJobId(null);
    } else if (jobStatus?.state === 'failed') {
      message.error(`健全度の再計算に失敗しました: ${jobStatus.failedReason}`);
    }
  }, [jobStatus, refetchMode, message]);

  useEffect(() => {
    if (currentMode) {
      setHealthManagementMode(currentMode);
    }
  }, [currentMode, setHealthManagementMode]);

  const handleModeChange = (value: 'complex' | 'manufacturer_lifespan') => {
    if (value !== currentMode) {
      setPendingMode(value);
      setShowWarning(true);
    }
  };

  const confirmModeChange = async () => {
    if (!pendingMode) return;

    try {
      const result = await updateHealthModeMutation.mutateAsync({ mode: pendingMode });
      setHealthManagementMode(pendingMode);
      setShowWarning(false);

      if (result.jobId) {
        setJobId(result.jobId);
        message.info('健全度管理方式を変更しました。設備の健全度を再計算しています...');
      } else {
        message.success('健全度管理方式を変更しました');
      }

      refetchMode();
      utils.company.getCurrentCompany.invalidate();
    } catch (error) {
      message.error('健全度管理方式の変更に失敗しました');
      console.error(error);
    }
  };

  if (!company) {
    return <div>会社情報を読み込み中...</div>;
  }

  return (
    <div>
      <Alert
        message="詳細設定"
        description="会社全体に適用される設定を管理します。変更は全ユーザーに影響します。"
        type="info"
        showIcon
        className="mb-4"
      />

      {/* ヘッダー画像設定 */}
      <Card title="ヘッダー画像" className="mb-4">
        <CompanyHeaderImageSettings companyId={company.id} isManager={isManager} />
      </Card>

      <Card title="部品発注承認・社印" className="mb-4">
        <CompanySealSettings companyId={company.id} isManager={isManager} isAdmin={isAdmin} />
      </Card>

      {/* 健全度管理設定 */}
      <Card title="健全度管理方式" className="mb-4">
        <Alert
          message="業界特性に応じた健全度計算方法"
          description="業界や設備の特性に応じて、最適な健全度計算方法を選択できます。"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Radio.Group
          value={currentMode}
          onChange={(e) => handleModeChange(e.target.value)}
          disabled={!isManager || updateHealthModeMutation.isPending}
          style={{ width: '100%' }}
        >
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card
              size="small"
              style={{
                border: currentMode === 'complex' ? '2px solid #1890ff' : '1px solid #d9d9d9',
                cursor: isManager ? 'pointer' : 'not-allowed',
                opacity: !isManager ? 0.6 : 1,
              }}
              onClick={() => isManager && handleModeChange('complex')}
            >
              <Radio value="complex" disabled={!isManager}>
                <Text strong style={{ fontSize: '15px' }}>健全度モード</Text>
              </Radio>
              <div style={{ color: '#666', marginLeft: 24, marginTop: 8, fontSize: '13px' }}>
                <div><strong>推奨業界:</strong> 工場、製造業、その他</div>
                <div><strong>特徴:</strong> 詳細な健全度予測が可能。環境係数、メンテナンス係数などのパラメータを設定できます。</div>
                <div><strong>計算式:</strong> ワイブル分布による高度な劣化率計算</div>
              </div>
            </Card>

            <Card
              size="small"
              style={{
                border: currentMode === 'manufacturer_lifespan' ? '2px solid #1890ff' : '1px solid #d9d9d9',
                cursor: isManager ? 'pointer' : 'not-allowed',
                opacity: !isManager ? 0.6 : 1,
              }}
              onClick={() => isManager && handleModeChange('manufacturer_lifespan')}
            >
              <Radio value="manufacturer_lifespan" disabled={!isManager}>
                <Text strong style={{ fontSize: '15px' }}>耐用年数モード</Text>
              </Radio>
              <div style={{ color: '#666', marginLeft: 24, marginTop: 8, fontSize: '13px' }}>
                <div><strong>推奨業界:</strong> 病院、ホテル、商業施設、オフィス建物など</div>
                <div><strong>特徴:</strong> シンプルで分かりやすい管理。メーカー指定の耐用年数を基準に計算します。</div>
                <div><strong>計算式:</strong> 経年劣化ベースの線形計算（耐用年数80%以降は加速）</div>
              </div>
            </Card>
          </Space>
        </Radio.Group>

        {isManager && (
          <Alert
            message="変更時の注意"
            description={
              <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                <li>健全度管理方式を変更すると、全ての設備の健全度が再計算されます</li>
                <li>既存のパラメータ設定（dpEul、k係数など）は保持されます</li>
              </ul>
            }
            type="warning"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}

        {/* ジョブ進捗状況の表示 */}
        {jobId && jobStatus && (
          <Card
            title="再計算処理中"
            style={{ marginTop: 16 }}
            bordered
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <Progress
                percent={jobStatus.progress || 0}
                status={
                  jobStatus.state === 'failed'
                    ? 'exception'
                    : jobStatus.state === 'completed'
                      ? 'success'
                      : 'active'
                }
              />
              <Text>状態: {jobStatus.state}</Text>
              {jobStatus.result && (
                <Text type="secondary">
                  {jobStatus.result.succeeded}/{jobStatus.result.total} 件完了
                  {jobStatus.result.failed > 0 && ` (${jobStatus.result.failed} 件失敗)`}
                </Text>
              )}
              {jobStatus.state === 'failed' && (
                <Alert type="error" message={jobStatus.failedReason} />
              )}
              {jobStatus.result?.errors && jobStatus.result.errors.length > 0 && (
                <Alert
                  type="warning"
                  message="一部の設備で再計算に失敗しました"
                  description={
                    <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                      {jobStatus.result.errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  }
                />
              )}
            </Space>
          </Card>
        )}
      </Card>

      {/* TODO: 通知設定以下は将来的に実装予定
      <Form
        form={form}
        layout="vertical"
        initialValues={currentSettings}
        disabled={!isManager}
      >
        <Card title="通知設定" className="mb-4">
          <Form.Item
            name="enableEmailNotifications"
            label="メール通知"
            valuePropName="checked"
          >
            <Switch checkedChildren="有効" unCheckedChildren="無効" />
          </Form.Item>

          <Form.Item
            name="enableMaintenanceReminders"
            label="メンテナンスリマインダー"
            valuePropName="checked"
          >
            <Switch checkedChildren="有効" unCheckedChildren="無効" />
          </Form.Item>

          <Form.Item
            name="enableDeteriorationAlerts"
            label="健全度アラート"
            valuePropName="checked"
          >
            <Switch checkedChildren="有効" unCheckedChildren="無効" />
          </Form.Item>

          <Form.Item
            name="enableMonthlyReports"
            label="月次レポート自動送信"
            valuePropName="checked"
          >
            <Switch checkedChildren="有効" unCheckedChildren="無効" />
          </Form.Item>
        </Card>

        <Card title="セキュリティ設定" className="mb-4">
          <Form.Item
            name="requireTwoFactor"
            label="二要素認証を必須にする"
            valuePropName="checked"
          >
            <Switch checkedChildren="有効" unCheckedChildren="無効" />
          </Form.Item>

          <Form.Item
            name="passwordExpirationDays"
            label="パスワード有効期限（日数）"
          >
            <Form.Item
              name="enablePasswordExpiration"
              valuePropName="checked"
              noStyle
            >
              <Switch checkedChildren="有効" unCheckedChildren="無効" />
            </Form.Item>
          </Form.Item>

          <Form.Item
            name="sessionTimeoutMinutes"
            label="セッションタイムアウト（分）"
          >
            <Form.Item
              name="enableSessionTimeout"
              valuePropName="checked"
              noStyle
            >
              <Switch checkedChildren="有効" unCheckedChildren="無効" />
            </Form.Item>
          </Form.Item>

          <Form.Item
            name="ipWhitelist"
            label="IPアドレス制限"
            valuePropName="checked"
          >
            <Switch checkedChildren="有効" unCheckedChildren="無効" />
          </Form.Item>
        </Card>

        <Card title="データ管理" className="mb-4">
          <Form.Item
            name="dataRetentionDays"
            label="データ保持期間（日数）"
          >
            <Form.Item
              name="enableDataRetention"
              valuePropName="checked"
              noStyle
            >
              <Switch checkedChildren="有効" unCheckedChildren="無効" />
            </Form.Item>
          </Form.Item>

          <Form.Item
            name="enableAutoBackup"
            label="自動バックアップ"
            valuePropName="checked"
          >
            <Switch checkedChildren="有効" unCheckedChildren="無効" />
          </Form.Item>

          <Form.Item
            name="enableAuditLog"
            label="監査ログ"
            valuePropName="checked"
          >
            <Switch checkedChildren="有効" unCheckedChildren="無効" />
          </Form.Item>
        </Card>

        <Card title="API設定" className="mb-4">
          <Form.Item
            name="enableAPI"
            label="API アクセス"
            valuePropName="checked"
          >
            <Switch checkedChildren="有効" unCheckedChildren="無効" />
          </Form.Item>

          <Form.Item
            name="apiRateLimitPerHour"
            label="APIレート制限（時間あたり）"
          >
            <Form.Item
              name="enableApiRateLimit"
              valuePropName="checked"
              noStyle
            >
              <Switch checkedChildren="有効" unCheckedChildren="無効" />
            </Form.Item>
          </Form.Item>

          <Form.Item
            name="enableWebhooks"
            label="Webhook通知"
            valuePropName="checked"
          >
            <Switch checkedChildren="有効" unCheckedChildren="無効" />
          </Form.Item>
        </Card>

        <Card title="システム設定" className="mb-4">
          <Form.Item
            name="maintenanceMode"
            label="メンテナンスモード"
            valuePropName="checked"
            help="有効にすると、管理者以外のアクセスが制限されます"
          >
            <Switch checkedChildren="有効" unCheckedChildren="無効" />
          </Form.Item>

          <Form.Item
            name="debugMode"
            label="デバッグモード"
            valuePropName="checked"
            help="開発用の詳細なログを出力します"
          >
            <Switch checkedChildren="有効" unCheckedChildren="無効" />
          </Form.Item>

          <Form.Item
            name="betaFeatures"
            label="ベータ機能"
            valuePropName="checked"
            help="実験的な新機能を有効にします"
          >
            <Switch checkedChildren="有効" unCheckedChildren="無効" />
          </Form.Item>
        </Card>

        {isManager && (
          <div className="flex justify-end">
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={updateMutation.isPending}
            >
              設定を保存
            </Button>
          </div>
        )}
      </Form>
      */}

      {/* 退会セクション（Freeプランかつ未課金の管理者のみ表示） */}
      {withdrawEligibility?.canWithdraw && company && (
        <>
          <Divider />
          <Card
            title={
              <Space>
                <LogoutOutlined style={{ color: '#ff4d4f' }} />
                <span>退会</span>
              </Space>
            }
            className="mb-4"
            styles={{
              header: { borderBottom: '1px solid #ffccc7' },
            }}
          >
            <Alert
              message="退会について"
              description={
                <div>
                  <Paragraph style={{ marginBottom: 8 }}>
                    退会すると、この会社に所属する全てのユーザーがログインできなくなります。
                  </Paragraph>
                  <Paragraph style={{ marginBottom: 0 }}>
                    退会後、データは削除されます。
                  </Paragraph>
                </div>
              }
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Button
              danger
              icon={<LogoutOutlined />}
              onClick={() => setWithdrawModalOpen(true)}
            >
              退会手続きを開始
            </Button>
          </Card>
        </>
      )}

      {/* 退会モーダル */}
      {company && (
        <WithdrawModal
          open={withdrawModalOpen}
          onClose={() => setWithdrawModalOpen(false)}
          companyName={company.name}
        />
      )}

      {/* 健全度管理方式変更の確認モーダル */}
      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#faad14' }} />
            <span>健全度管理方式の変更</span>
          </Space>
        }
        open={showWarning}
        onOk={confirmModeChange}
        onCancel={() => {
          setShowWarning(false);
          setPendingMode(null);
        }}
        okText="変更する"
        cancelText="キャンセル"
        confirmLoading={updateHealthModeMutation.isPending}
        width={600}
      >
        <Alert
          type="warning"
          showIcon
          message="重要な変更です"
          description={
            <div>
              <Paragraph>
                健全度管理方式を <Text strong>{currentMode === 'complex' ? '健全度モード' : '耐用年数モード'}</Text> から{' '}
                <Text strong>{pendingMode === 'complex' ? '健全度モード' : '耐用年数モード'}</Text> に変更します。
              </Paragraph>
              <Paragraph>
                <strong>変更による影響:</strong>
              </Paragraph>
              <ul>
                <li>既存の設備データの表示方法が変わります</li>
                <li>全ての設備の健全度が再計算されます</li>
                <li>健全度予測ページのUIが切り替わります</li>
                <li>設備フォームで表示される項目が変わります</li>
              </ul>
              <Paragraph strong>
                この変更を続けますか？
              </Paragraph>
            </div>
          }
          style={{ marginTop: 16 }}
        />
      </Modal>
    </div>
  );
};

export default CompanySettingsTab;
